import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { Home, ChevronLeft, ChevronRight, RefreshCw, CalendarClock, GripVertical, X, AlertTriangle } from 'lucide-react';
import Timeline, { type BatchFeasibilityHint } from './Timeline';
import BatchFormModal, { batchTimeLabel, type BatchDraft } from './BatchFormModal';
import { usePlanning, useCreateBatch, useMoveBatch, useDeleteBatch, useBatchSchedule } from '../../api/planning';
import { useWorkOrders, useBatchDetail, useBatchDetails, useAssignOrder, useUnassignOrder } from '../../api/fournees';
import { useCycles } from '../../api/referentiel';
import { useCapabilities } from '../../api/capabilities';
import { ApiError } from '../../api/client';
import type { PlanningBatch, WorkOrderListItem } from '../../api/types';
import { AlertList, Gauge, severityDot } from '../fournees/alerts';
import { alertIcon } from '../fournees/alerts';
import { ProgressBar, statusDot } from '../fournees/status';
import { evaluateFeasibility, type Feasibility } from '../fournees/feasibility';
import { cycleColor } from './colors';

const HORIZON_DAYS = 3;
const startOfUtcDay = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

/**
 * Plan de travail fusionné (planning + affectation).
 *
 * Haut : timeline — on crée/déplace/supprime des fournées, on y dépose des OF.
 * Bas  : liste des OF (gauche) + détail de la fournée sélectionnée (droite).
 *
 * Sélection croisée :
 * - sélectionner une fournée filtre les OF compatibles avec son cycle ;
 * - sélectionner (ou glisser) un OF met en avant les fournées compatibles et
 *   grise les autres, avec des icônes de faisabilité (Tc bloquant, voies/délai).
 */
export default function WorkspaceView() {
  const [anchor, setAnchor] = useState(() => startOfUtcDay(new Date()));
  const from = anchor;
  const to = useMemo(() => new Date(anchor.getTime() + HORIZON_DAYS * 86_400_000), [anchor]);

  const { data: planning, isLoading, error, isFetching, refetch } = usePlanning(from.toISOString(), to.toISOString(), { refetchMs: 60_000 });
  const { data: cycles } = useCycles();
  const { data: unassigned } = useWorkOrders({ assigned: false });
  const { tcFin } = useCapabilities();
  const create = useCreateBatch();
  const move = useMoveBatch();
  const remove = useDeleteBatch();
  const assign = useAssignOrder();

  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [selectedOf, setSelectedOf] = useState<string | null>(null);
  const [checkedCodes, setCheckedCodes] = useState<Set<string>>(new Set());
  const [activeDragOf, setActiveDragOf] = useState<string | null>(null);
  const [draft, setDraft] = useState<BatchDraft | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  function toggleChecked(code: string) {
    setCheckedCodes((s) => {
      const next = new Set(s);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  /** Affecte une liste d'OF à une fournée (bouton / sélection multiple). */
  function assignCodes(codes: string[], batchId: number) {
    setRefusal(null);
    let refused = 0;
    codes.forEach((codeOf) =>
      assign.mutate(
        { batchId, codeOf },
        {
          onError: (err) => {
            refused += 1;
            const msg = err instanceof ApiError && Array.isArray(err.details) ? (err.details as { message: string }[])[0]?.message : null;
            setRefusal(`${refused} OF refusé(s)${msg ? ` : ${msg}` : ''}.`);
          },
        },
      ),
    );
    setCheckedCodes(new Set());
  }

  const batches = planning?.batches ?? [];
  const details = useBatchDetails(batches.map((b) => b.id));
  const selectedBatch = batches.find((b) => b.id === selectedBatchId) ?? null;

  // Couleur par cycle (paramétrée au cycle, sinon dérivée du code).
  const cycleColorByCode = useMemo(() => new Map((cycles ?? []).map((c) => [c.code, cycleColor(c.code, c.color)])), [cycles]);
  // Couleur d'un OF non affecté : celle de la fournée sélectionnée si elle
  // filtre la liste, sinon celle de son premier cycle autorisé.
  function ofColor(of: WorkOrderListItem): string {
    if (selectedBatch) return cycleColor(selectedBatch.cycleCode, selectedBatch.cycleColor);
    const first = of.allowedCycleCodes[0];
    return first ? cycleColorByCode.get(first) ?? '#9ca3af' : '#9ca3af';
  }

  // L'OF « actif » pour la mise en avant : celui glissé, sinon celui cliqué.
  const highlightOfCode = activeDragOf ?? selectedOf;
  const highlightOf = unassigned?.find((o) => o.codeOf === highlightOfCode) ?? null;

  // Faisabilité par fournée quand un OF est mis en avant.
  const feasibilityByBatch = useMemo<Record<number, BatchFeasibilityHint>>(() => {
    if (!highlightOf) return {};
    const map: Record<number, BatchFeasibilityHint> = {};
    for (const b of batches) {
      const d = details.get(b.id);
      const counters = d?.counters ?? { tcUsed: 0, tcMax: 0, vacuumUsed: 0, vacuumMax: 0 };
      const f = evaluateFeasibility(highlightOf, { cycleCode: b.cycleCode, t3: b.t3, counters }, { tcFin });
      map[b.id] = { level: f.level, icons: feasibilityIcons(f) };
    }
    return map;
  }, [highlightOf, batches, details, tcFin]);

  // OF affichés dans la liste : si une fournée est sélectionnée, on filtre sur
  // son cycle ; sinon on montre tous les OF en attente.
  const listedOfs = useMemo(() => {
    const all = unassigned ?? [];
    if (!selectedBatch) return all;
    return all.filter((o) => o.allowedCycleCodes.includes(selectedBatch.cycleCode));
  }, [unassigned, selectedBatch]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function onDragStart(e: DragStartEvent) {
    setRefusal(null);
    setActiveDragOf(String(e.active.id));
  }
  function onDragEnd(e: DragEndEvent) {
    setActiveDragOf(null);
    const codeOf = String(e.active.id);
    const overId = e.over?.id;
    if (typeof overId !== 'string' || !overId.startsWith('batch-')) return;
    const batchId = Number(overId.slice('batch-'.length));
    assign.mutate(
      { batchId, codeOf },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.status === 422) {
            const d = Array.isArray(err.details) ? (err.details as { message: string }[])[0]?.message : null;
            setRefusal(d ?? err.message);
          } else setRefusal("Échec de l'affectation.");
        },
      },
    );
  }

  function openCreate(resourceId: number, at: Date) {
    const rounded = new Date(Math.round(at.getTime() / (15 * 60_000)) * 15 * 60_000);
    setDraft({ resourceId, cycleId: cycles?.[0]?.id ?? 0, t0: rounded.toISOString() });
  }
  function submitDraft(data: BatchDraft) {
    const done = () => { create.reset(); move.reset(); setDraft(null); };
    if (data.id) move.mutate({ id: data.id, data: { resourceId: data.resourceId, cycleId: data.cycleId, t0: data.t0 } }, { onSuccess: done });
    else create.mutate({ resourceId: data.resourceId, cycleId: data.cycleId, t0: data.t0 }, { onSuccess: done });
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-blue-800 text-white">
        <div className="px-4 py-3 flex justify-between items-center">
          <h1 className="text-xl font-bold flex items-center gap-2"><CalendarClock size={20} /> Planning & affectation</h1>
          <div className="flex items-center gap-4">
            <button onClick={() => refetch()} className="flex items-center gap-2 text-blue-100 hover:text-white">
              <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} /> Actualiser
            </button>
            <Link to="/" className="flex items-center gap-2 text-blue-100 hover:text-white"><Home size={18} /> Accueil</Link>
          </div>
        </div>
      </header>

      <div className="px-4 py-2 flex items-center gap-2 border-b bg-white">
        <button className="p-2 rounded border hover:bg-gray-50" onClick={() => setAnchor((a) => new Date(a.getTime() - 86_400_000))} aria-label="Précédent"><ChevronLeft size={18} /></button>
        <button className="px-3 py-2 rounded border hover:bg-gray-50 text-sm" onClick={() => setAnchor(startOfUtcDay(new Date()))}>Aujourd'hui</button>
        <button className="p-2 rounded border hover:bg-gray-50" onClick={() => setAnchor((a) => new Date(a.getTime() + 86_400_000))} aria-label="Suivant"><ChevronRight size={18} /></button>
        <span className="ml-2 text-sm text-gray-600">{from.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })} → {new Date(to.getTime() - 1).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })}</span>
        <span className="ml-auto text-xs text-gray-500">Cliquez une piste libre pour créer · une fournée pour la remplir · glissez un OF vers une fournée.</span>
      </div>

      {refusal && (
        <div className="mx-4 mt-3 bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> Dépôt refusé : {refusal}
          <button className="ml-auto" onClick={() => setRefusal(null)} aria-label="Fermer"><X size={14} /></button>
        </div>
      )}

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <main className="p-4 space-y-4">
          {isLoading && <p className="text-gray-500">Chargement du planning…</p>}
          {error && <p className="text-red-600">Impossible de charger le planning.</p>}

          {planning && (
            <Timeline
              resources={planning.resources}
              batches={planning.batches}
              from={from}
              to={to}
              now={new Date()}
              selectedBatchId={selectedBatchId}
              droppableBatches
              feasibilityByBatch={highlightOf ? feasibilityByBatch : undefined}
              onEmptyClick={openCreate}
              onBatchClick={(b) => { setSelectedBatchId((id) => (id === b.id ? null : b.id)); setSelectedOf(null); }}
            />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
            {/* OF en attente (filtrés si une fournée est sélectionnée) */}
            <section className="bg-white rounded-lg shadow p-3 h-fit">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-semibold text-gray-700">OF en attente ({listedOfs.length})</h2>
                {selectedBatch && <span className="text-xs text-blue-600">cycle {selectedBatch.cycleCode}</span>}
              </div>
              <p className="text-xs text-gray-400 mb-2">
                {selectedBatch ? 'Glissez, ou cochez puis « Affecter ». Cliquez un OF pour voir les fournées compatibles.' : 'Sélectionnez une fournée pour affecter, ou un OF pour voir les fournées compatibles.'}
              </p>

              {/* Barre d'action : affectation par bouton de la sélection multiple */}
              {selectedBatch && checkedCodes.size > 0 && (
                <div className="mb-2 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                  <span className="text-xs text-blue-800">{checkedCodes.size} coché(s)</span>
                  <button
                    className="ml-auto text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                    onClick={() => assignCodes([...checkedCodes].filter((c) => listedOfs.some((o) => o.codeOf === c)), selectedBatch.id)}
                  >
                    Affecter à {selectedBatch.number}
                  </button>
                  <button className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setCheckedCodes(new Set())}>Vider</button>
                </div>
              )}

              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {listedOfs.map((of) => (
                  <DraggableOrder
                    key={of.codeOf}
                    order={of}
                    color={ofColor(of)}
                    selected={selectedOf === of.codeOf}
                    checked={checkedCodes.has(of.codeOf)}
                    onCheck={() => toggleChecked(of.codeOf)}
                    onSelect={() => { setSelectedOf((c) => (c === of.codeOf ? null : of.codeOf)); }}
                    onAssign={selectedBatch ? () => assignCodes([of.codeOf], selectedBatch.id) : undefined}
                  />
                ))}
                {listedOfs.length === 0 && <p className="text-sm text-gray-400">Aucun OF {selectedBatch ? 'compatible' : 'en attente'}.</p>}
              </div>
            </section>

            {/* Détail de la fournée sélectionnée */}
            <section>
              {selectedBatch ? (
                <SelectedBatchPanel batch={selectedBatch} onClear={() => setSelectedBatchId(null)} onEdit={() => setDraft({ id: selectedBatch.id, resourceId: selectedBatch.resourceId, cycleId: selectedBatch.cycleId, t0: selectedBatch.t0 })} onDelete={() => { if (confirm(`Supprimer ${selectedBatch.number} ?`)) { remove.mutate(selectedBatch.id); setSelectedBatchId(null); } }} />
              ) : (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400 h-full flex items-center justify-center">
                  Sélectionnez une fournée pour la remplir, ou un OF pour repérer les fournées compatibles.
                </div>
              )}
            </section>
          </div>
        </main>
      </DndContext>

      {draft && (
        <BatchFormModal
          draft={draft}
          resources={planning?.resources ?? []}
          cycles={cycles ?? []}
          saving={create.isPending || move.isPending}
          error={create.error ?? move.error}
          onClose={() => { create.reset(); move.reset(); setDraft(null); }}
          onSubmit={submitDraft}
        />
      )}
    </div>
  );
}

/** Icônes de faisabilité à afficher sur la fournée (hors incompatible). */
function feasibilityIcons(f: Feasibility): string[] {
  return f.reasons.map((r) => alertIcon(r.code));
}

const PRIORITY_DOT: Record<string, string> = { HIGH: 'bg-red-500', MEDIUM: 'bg-amber-400', LOW: 'bg-gray-300' };

/**
 * Carte d'OF : TOUTE la carte est draggable (dnd-kit distingue un clic d'un
 * glissement via le seuil de 5 px). Le clic sélectionne l'OF ; la coche et le
 * bouton d'affectation offrent une alternative au glisser-déposer.
 */
function DraggableOrder({ order, color, selected, checked, onCheck, onSelect, onAssign }: {
  order: WorkOrderListItem;
  color: string;
  selected: boolean;
  checked: boolean;
  onCheck: () => void;
  onSelect: () => void;
  onAssign?: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: order.codeOf });
  const stop = (e: React.SyntheticEvent) => { e.stopPropagation(); };
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onSelect}
      style={{ borderLeftColor: color }}
      className={`border-l-4 bg-white border rounded pl-2 pr-1.5 py-1.5 cursor-grab active:cursor-grabbing ${selected ? 'ring-2 ring-blue-500' : 'hover:shadow-sm'} ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center gap-1.5">
        {/* La coche ne doit ni sélectionner ni démarrer un glissement. */}
        <input
          type="checkbox"
          checked={checked}
          onChange={onCheck}
          onClick={stop}
          onPointerDown={stop}
          className="shrink-0"
          aria-label={`Cocher ${order.codeOf}`}
        />
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${PRIORITY_DOT[order.priority]}`} title={`Priorité ${order.priority}`} />
        <div className="min-w-0 flex-grow">
          <div className="text-sm font-medium text-gray-800 truncate">{order.codeOf} <span className="text-xs text-gray-400 font-normal">{order.quantity}×</span></div>
          <div className="text-[11px] text-gray-500 truncate">{order.label ?? order.productRef}</div>
          <div className="text-[10px] text-gray-400">cycles : {order.allowedCycleCodes.join(', ') || '—'}</div>
        </div>
        {onAssign && (
          <button
            className="shrink-0 text-blue-600 hover:text-blue-800 text-xs px-1.5 py-1 rounded hover:bg-blue-50"
            title="Affecter à la fournée sélectionnée"
            onClick={(e) => { stop(e); onAssign(); }}
            onPointerDown={stop}
          >
            → affecter
          </button>
        )}
        <GripVertical size={14} className="text-gray-300 shrink-0" />
      </div>
    </div>
  );
}

/** Panneau de détail de la fournée sélectionnée : compteurs, alertes, OF affectés. */
function SelectedBatchPanel({ batch, onClear, onEdit, onDelete }: { batch: PlanningBatch; onClear: () => void; onEdit: () => void; onDelete: () => void }) {
  const { effectif } = useCapabilities();
  const { data: detail } = useBatchDetail(batch.id);
  const { data: schedule } = useBatchSchedule(effectif ? batch.id : null);
  const unassignOrder = useUnassignOrder();
  const batchAlerts = detail?.alerts.filter((a) => !a.codeOf) ?? [];

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${severityDot(detail?.severity ?? 'none')}`} />
          <span className="font-semibold text-gray-800">{batch.number}</span>
          <span className="text-xs text-gray-500">{batch.cycleLabel} · {batchTimeLabel(batch)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onEdit} className="text-blue-600 hover:text-blue-800 text-sm">Déplacer</button>
          <button onClick={onDelete} className="text-red-600 hover:text-red-800 text-sm">Supprimer</button>
          <button onClick={onClear} className="text-gray-400 hover:text-gray-700" aria-label="Fermer"><X size={16} /></button>
        </div>
      </div>

      {/* Avancement global de la fournée */}
      <div className="px-4 pt-3">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-0.5">
          <span>Avancement global</span>
          <span className="font-semibold text-gray-700">{detail?.progressPct ?? 0}%</span>
        </div>
        <ProgressBar pct={detail?.progressPct ?? 0} height={8} />
      </div>

      <div className="p-4 grid grid-cols-2 gap-4 max-w-md">
        <Gauge label="Tc" used={detail?.counters.tcUsed ?? 0} max={detail?.counters.tcMax ?? 0} />
        <Gauge label="Voies" used={detail?.counters.vacuumUsed ?? 0} max={detail?.counters.vacuumMax ?? 0} />
      </div>

      {batchAlerts.length > 0 && <div className="px-4 pb-2"><AlertList alerts={batchAlerts} /></div>}

      <div className="px-4 pb-4">
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">OF affectés ({detail?.items.length ?? 0})</h4>
        <div className="space-y-2">
          {detail?.items.map((it) => (
            <div key={it.codeOf} className="group">
              <div className="flex items-center gap-1.5 text-sm">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusDot(it.statusCategory)}`} title={it.statusCategory} />
                <span className="text-gray-800 font-medium">{it.codeOf}</span>
                <span className="text-gray-400 text-xs truncate">{it.label}</span>
                {it.alerts.length > 0 && <span className="text-xs" title={it.alerts.map((a) => a.message).join(' · ')}>{it.alerts.map((a) => alertIcon(a.code)).join('')}</span>}
                <span className="ml-auto text-xs text-gray-500 tabular-nums shrink-0">{it.progressPct}%</span>
                <button className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 shrink-0" onClick={() => unassignOrder.mutate({ batchId: batch.id, codeOf: it.codeOf })} aria-label="Retirer"><X size={14} /></button>
              </div>
              <div className="ml-4 mt-0.5"><ProgressBar pct={it.progressPct} category={it.statusCategory} height={4} /></div>
            </div>
          ))}
          {detail && detail.items.length === 0 && <p className="text-xs text-gray-400 italic">Aucun OF. Glissez-en depuis la liste.</p>}
        </div>

        {effectif && schedule && schedule.length > 0 && (
          <div className="mt-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Rétro-planning</h4>
            {schedule.map((of) => (
              <div key={of.codeOf} className="text-xs text-gray-600 flex justify-between">
                <span>{of.codeOf}</span>
                <span className="text-gray-400">{of.operations.map((o) => o.workCenterCode).join(' → ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
